"use client";

import { format } from "date-fns";
import { useRef, useState, type FormEvent } from "react";

import { FieldError } from "@/components/shared/field";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { publicSearchRequestSchema } from "@/lib/gift-list-search/public-search-schemas";
import type { SearchCriteria } from "@/lib/gift-list-search/public-search-types";

interface GiftListSearchFormProps {
  /** Seeds the name field on mount. The field stays uncontrolled: submit reads the DOM. */
  defaultQuery?: string;
  /** Seeds the locality field on mount, the same way. */
  defaultEventLocation?: string;
  disabled?: boolean;
  onSubmit(criteria: SearchCriteria): void;
}

interface FormErrors {
  query?: string;
  eventDateFrom?: string;
  eventLocation?: string;
}

export function GiftListSearchForm({
  defaultQuery = "",
  defaultEventLocation = "",
  disabled = false,
  onSubmit,
}: GiftListSearchFormProps) {
  const [errors, setErrors] = useState<FormErrors>({});
  const [eventDate, setEventDate] = useState<Date>();
  // Whether the name field holds anything: it decides the inline "Limpar" and, with the date and
  // the place, whether there is a search to run at all (the reference's `podeBuscar`).
  const [hasQuery, setHasQuery] = useState(defaultQuery.trim().length > 0);
  const [hasEventLocation, setHasEventLocation] = useState(defaultEventLocation.trim().length > 0);
  // Any one of the three is enough, which is what having a locality parameter bought us.
  const canSearch = hasQuery || eventDate !== undefined || hasEventLocation;
  const queryRef = useRef<HTMLInputElement>(null);
  const eventDateRef = useRef<HTMLInputElement>(null);
  const eventLocationRef = useRef<HTMLInputElement>(null);

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const query = String(data.get("query") ?? "");
    const eventDateFrom = String(data.get("eventDateFrom") ?? "");
    const eventLocation = String(data.get("eventLocation") ?? "");
    const parsed = publicSearchRequestSchema.safeParse({
      query,
      ...(eventDateFrom.length === 0 ? {} : { eventDateFrom }),
      ...(eventLocation.length === 0 ? {} : { eventLocation }),
      page: 1,
    });

    if (!parsed.success) {
      const nextErrors: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0];
        if (field === "query" && nextErrors.query === undefined) {
          nextErrors.query = issue.message;
        }
        if (
          field === "eventDateFrom" &&
          nextErrors.eventDateFrom === undefined
        ) {
          nextErrors.eventDateFrom = issue.message;
        }
        if (field === "eventLocation" && nextErrors.eventLocation === undefined) {
          nextErrors.eventLocation = issue.message;
        }
      }
      setErrors(nextErrors);
      if (nextErrors.query !== undefined) queryRef.current?.focus();
      else if (nextErrors.eventDateFrom !== undefined) {
        eventDateRef.current?.focus();
      } else if (nextErrors.eventLocation !== undefined) {
        eventLocationRef.current?.focus();
      }
      return;
    }

    setErrors({});
    onSubmit({
      ...(parsed.data.query === undefined ? {} : { query: parsed.data.query }),
      ...(parsed.data.eventDateFrom === undefined
        ? {}
        : { eventDateFrom: parsed.data.eventDateFrom }),
      ...(parsed.data.eventLocation === undefined
        ? {}
        : { eventLocation: parsed.data.eventLocation }),
    });
  }

  return (
    <form
      aria-label="Buscar listas de presentes"
      className="tb-public-search-form"
      noValidate
      onSubmit={handleSubmit}
    >
      <div className="min-w-0">
        <Label className="sr-only" htmlFor="public-list-query">
          Buscar por nome do casal, do anfitrião ou da lista
        </Label>
        <div
          className="tb-public-search-query"
          data-public-search-tour-target="query"
          data-invalid={errors.query === undefined ? undefined : true}
        >
          <Icon name="search" size={24} />
          <Input
            ref={queryRef}
            id="public-list-query"
            name="query"
            autoComplete="off"
            className="tb-public-search-query-input"
            defaultValue={defaultQuery}
            disabled={disabled}
            placeholder="Digite o nome do casal, do anfitrião ou da lista"
            aria-describedby="public-list-query-error"
            aria-invalid={errors.query === undefined ? undefined : true}
            onChange={(event) => setHasQuery(event.currentTarget.value.trim().length > 0)}
          />
          {hasQuery ? (
            <button
              type="button"
              className="tb-public-search-query-clear"
              disabled={disabled}
              onClick={() => {
                const input = queryRef.current;
                if (input === null) return;
                input.value = "";
                setHasQuery(false);
                setErrors((current) => ({ ...current, query: undefined }));
                input.focus();
              }}
            >
              Limpar
            </button>
          ) : null}
        </div>
        {/* No reserved slot at rest: the reference's form has none, so its rhythm (34px to the
            filters, 32px to the action) holds until an error actually shows. */}
        <FieldError id="public-list-query-error" className="empty:hidden">{errors.query}</FieldError>
      </div>

      <div
        className="tb-public-search-refinements"
        data-public-search-tour-target="refinements"
      >
        <div className="tb-public-search-filter">
          <Label htmlFor="public-list-event-date">Data do evento</Label>
          <DatePicker
            ref={eventDateRef}
            id="public-list-event-date"
            title="Data do evento"
            value={eventDate}
            onValueChange={(date) => {
              setEventDate(date);
              setErrors((current) => ({
                ...current,
                eventDateFrom: undefined,
              }));
            }}
            // An optional filter: erasing the date takes it out of the search.
            onClear={() => setEventDate(undefined)}
            placeholder="Selecione a data do evento"
            className="tb-public-search-date-trigger !text-base"
            defaultMonth={new Date()}
            dialogClassName="tb-public-search-date-dialog"
            dialogOverlayClassName="tb-public-search-date-overlay"
            disabled={disabled}
            aria-describedby="public-list-event-date-error"
            invalid={errors.eventDateFrom !== undefined}
          />
          <input
            type="hidden"
            name="eventDateFrom"
            value={eventDate === undefined ? "" : format(eventDate, "yyyy-MM-dd")}
            disabled={disabled}
          />
          <FieldError id="public-list-event-date-error" className="empty:hidden">
            {errors.eventDateFrom}
          </FieldError>
        </div>

        {/*
          Free text, not a Select.
          
          It was a **disabled** Select with the single option "Localidade indisponível" while the
          public contract had no locality parameter — the honest shape then, since inventing
          options would have promised a filter that did not exist.
          
          The backend gave it its own parameter on 2026-10-09, ANDed with the free text, so a
          name and a place can be asked for together. It stays free text because the place is
          free text on the list too (`event_location`, "como está no convite"): a Select would
          need a vocabulary of places that nothing in the product has.
        */}
        <div className="tb-public-search-filter">
          <Label htmlFor="public-list-locality">Localidade</Label>
          <Input
            ref={eventLocationRef}
            id="public-list-locality"
            name="eventLocation"
            autoComplete="off"
            className="!text-base"
            defaultValue={defaultEventLocation}
            disabled={disabled}
            placeholder="Cidade, ou o nome do lugar"
            aria-describedby="public-list-locality-error"
            aria-invalid={errors.eventLocation === undefined ? undefined : true}
            onChange={(event) =>
              setHasEventLocation(event.currentTarget.value.trim().length > 0)
            }
          />
          <FieldError id="public-list-locality-error" className="empty:hidden">
            {errors.eventLocation}
          </FieldError>
        </div>
      </div>

      <div
        className="tb-public-search-form-action"
        data-public-search-tour-target="submit"
      >
        {/* Nothing to search with until there is a name or a date: the reference's disabled
            submit, at 40% (`naoPodeBuscar`). */}
        <Button className="tb-public-search-submit" disabled={disabled || !canSearch} type="submit">
          Buscar listas
        </Button>
      </div>
    </form>
  );
}
